import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { isThankYouLotto } from '../../../utils/template';
import { validateLineBets } from '../Soccer/utils/validate';
import { LotteryTypes } from '../Types/Enums';
import { getNonEmptyLines, isNonEmptyLine, isValidLine } from '../helpers/linesHelper';
import useLinesHelper from '../helpers/useLinesHelper';
import { RootState } from '../redux/store';
import * as action from '../redux/storeSlice';

enum Confirmation {
    Unrequired = 0,
    WrongLine = 1,
    NotFilledUp = 2,
    // the following messages have not been moved to react because they are currently unnecessary:
    // LotteryNotSelected = 3; You have to choose the lottery you wish to play.
    // NotEnoughMoney = 4; To purchase tickets you need to make a deposit first.
}

export default function PurchaseConfirmation() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const div = useRef<HTMLDivElement>();
    const [confirmation, setConfirmation] = useState<Confirmation>(Confirmation.Unrequired);
    const { betMatchLines } = useSelector((state: RootState) => state.soccerPlayStore);
    const { lottery, lines, numbersPerLine, numbersToPlay } = useLinesHelper();
    const [confirmDisabled, setConfirmDisabled] = useState<boolean>(false);
    const { pickedNumbers } = useSelector((state: RootState) => state.playStore);
    const isTemplateThankYouLotto = useMemo(() => isThankYouLotto(), []);

    const checkWrongLine = (): boolean => {
        switch (lottery.type) {
            case LotteryTypes.Soccer:
                return betMatchLines.some(
                    (line) => !validateLineBets(line) && line.some((bets) => bets.some((bet) => bet))
                );

            case LotteryTypes.Keno:
                return lines.some((line) => isNonEmptyLine(line) && !isValidLine(lottery, line, [numbersToPlay]));

            case LotteryTypes.Pick:
                return pickedNumbers.length > 0;

            case LotteryTypes.Lottery:
            default:
                return lines.some((line) => isNonEmptyLine(line) && !isValidLine(lottery, line, numbersPerLine));
        }
    };

    const checkMinLinesRequirement = (): boolean => {
        return lottery.minLinesPerTicket > getNonEmptyLines(lines).length;
    };

    const checkConfirmationStatus = (): Confirmation => {
        switch (true) {
            case checkMinLinesRequirement():
                return Confirmation.NotFilledUp;
            case checkWrongLine():
                return Confirmation.WrongLine;

            default:
                return Confirmation.Unrequired;
        }
    };

    useEffect(() => {
        const form = div.current.closest('form');

        if (!form) {
            return console.error('This component must be inside a form element!');
        }

        function handleSubmit(event: SubmitEvent) {
            const status = checkConfirmationStatus();
            setConfirmation(status);

            if (status !== Confirmation.Unrequired) {
                event.preventDefault();
            }
        }

        form.addEventListener('submit', handleSubmit);

        return () => form.removeEventListener('submit', handleSubmit);
    }, [lottery, lines, betMatchLines, numbersPerLine, numbersToPlay, pickedNumbers]);

    const handleConfirm = () => {
        if (!confirmDisabled) {
            setConfirmDisabled(true);
            div.current.closest('form').submit();
        }
    };

    const handleClose = () => {
        setConfirmation(Confirmation.Unrequired);
        dispatch(action.setPurchaseButtonDisabled(false));
    };

    const modal = {
        title: {
            [Confirmation.NotFilledUp]: t('playPicker.modal.minLinesTitle'),
            [Confirmation.WrongLine]: t('playPicker.modal.wrongLineTitle'),
        },
        message: {
            [Confirmation.NotFilledUp]: t('playPicker.modal.minLinesMessage', { minLines: lottery.minLinesPerTicket }),
            [Confirmation.WrongLine]: t('playPicker.modal.wrongLineMessage'),
        },
    };

    return (
        <>
            <div ref={div} />
            <Modal show={confirmation !== Confirmation.Unrequired} className="modals play-modals" onHide={handleClose}>
                <Modal.Header>
                    <h5 className="modal-title">{modal.title[confirmation]}</h5>

                    <button
                        type="button"
                        className={
                            isTemplateThankYouLotto
                                ? 'btn-close btn-close-white position-absolute top-0 end-0 m-3'
                                : 'close'
                        }
                        onClick={handleClose}
                    >
                        {!isTemplateThankYouLotto && <span aria-hidden="true">×</span>}
                        <span className="sr-only">Close</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <div className="message">
                        <p>{modal.message[confirmation]}</p>
                    </div>
                    <div className="modal-footer mb-0">
                        {!isTemplateThankYouLotto && (
                            <button type="button" className="btn btn-grey btn-submit mb-0" onClick={handleClose}>
                                {t('playPicker.modal.back')}
                            </button>
                        )}
                        {confirmation === Confirmation.WrongLine && (
                            <button
                                type="button"
                                className="btn btn-primary btn-submit mb-0"
                                disabled={confirmDisabled}
                                onClick={handleConfirm}
                            >
                                {t('playPicker.modal.continue')}
                            </button>
                        )}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
}
